import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/SEO/JsonLd';
import { siteStats } from '@/mocks/homeMocks';

const contactFormUrl = 'http://knn08.com/';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('company_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim()) {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(contactFormUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else if (serverMsg.toLowerCase().includes('spam') || serverMsg.toLowerCase().includes('form data is spam')) {
        setFormStatus('error');
        setFormError('提交失败，请稍后重试');
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫大全',
    url: 'http://knn08.com/',
    logo: 'http://knn08.com/logo.png',
    description: '韩漫大全免费平台是韩国漫画在线阅读网标杆，每日更新最新章节，提供海量韩漫资源，支持评论与收藏功能。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'hello@knn08.com',
    },
    sameAs: ['http://knn08.com/'],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '韩漫大全是什么平台？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫大全是专业的韩国漫画在线阅读网，提供海量韩漫资源免费阅读，每日更新最新章节。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系韩漫大全进行商务合作？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过本页面底部的联系表单提交合作意向，或发送邮件至 hello@knn08.com，我们将在3个工作日内回复。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫大全是否支持广告投放？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，我们为合作伙伴提供多种广告投放方案，包括首页焦点图、分类页推荐位、详情页插屏等。欢迎联系商务团队了解详情。',
        },
      },
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://knn08.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'http://knn08.com/about' },
    ],
  };

  return (
    <>
      <JsonLd data={[orgSchema, faqSchema, breadcrumbSchema]} />
      <Navbar />
      <main>
        {/* Hero / Brand Intro */}
        <section className="pt-24 pb-16 md:pb-20 bg-background-100">
          <div className="px-4 md:px-6">
            <nav aria-label="面包屑导航" className="mb-6">
              <ol className="flex items-center gap-2 text-sm text-foreground-600 flex-wrap">
                <li><Link to="/" className="hover:text-primary-600 transition-colors">首页</Link></li>
                <li><i className="ri-arrow-right-s-line text-foreground-400"></i></li>
                <li className="text-foreground-950 font-medium">关于我们</li>
              </ol>
            </nav>

            <div className="max-w-3xl">
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-6">
                关于韩漫大全
              </h1>
              <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-6">
                韩漫大全免费平台创立于2018年，是<strong>韩国漫画在线阅读网</strong>领域的标杆平台。我们致力于为全球中文读者提供最全面、最及时的韩漫阅读服务，每日更新超过50部热门韩漫的最新章节，让每一位漫画爱好者都能第一时间追到自己喜欢的作品。
              </p>
              <p className="text-base md:text-lg text-foreground-700 leading-relaxed">
                从创立之初，我们就坚持「免费、及时、高清、社区化」的理念。截至目前，平台已累计收录超过<strong>50,000部</strong>韩国漫画，服务超过<strong>1,000万名</strong>注册用户，日均访问量突破<strong>300万次</strong>。
              </p>
            </div>
          </div>
        </section>

        {/* Platform Stats */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                平台数据
              </h2>
              <p className="text-sm md:text-base text-foreground-600">
                用数字见证韩漫大全的成长与实力
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {[
                { label: '漫画总数', value: `${(siteStats.totalComics / 10000).toFixed(1)}万+`, icon: 'ri-book-3-line', color: 'bg-primary-100 text-primary-700' },
                { label: '累计用户', value: `${(siteStats.totalUsers / 100000000).toFixed(1)}亿+`, icon: 'ri-user-3-line', color: 'bg-accent-100 text-accent-700' },
                { label: '日均更新', value: `${siteStats.dailyUpdates}+`, icon: 'ri-refresh-line', color: 'bg-secondary-100 text-secondary-700' },
                { label: '用户评分', value: `${siteStats.avgRating}`, icon: 'ri-star-fill', color: 'bg-yellow-50 text-yellow-700' },
              ].map((stat) => (
                <div key={stat.label} className="rounded-2xl bg-background-100 p-6 md:p-8 text-center border border-background-200/70">
                  <div className={`w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-xl ${stat.color} mx-auto mb-4`}>
                    <i className={`${stat.icon} text-xl md:text-2xl`}></i>
                  </div>
                  <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-1">{stat.value}</div>
                  <div className="text-sm text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                为什么选择韩漫大全
              </h2>
              <p className="text-sm md:text-base text-foreground-600">
                韩漫大全在线阅读网的核心优势
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {[
                { icon: 'ri-flashlight-line', title: '极速更新', desc: '韩国发布后2小时内同步更新，全网最快' },
                { icon: 'ri-vip-diamond-line', title: '完全免费', desc: '所有漫画永久免费阅读，无需任何付费' },
                { icon: 'ri-image-line', title: '高清画质', desc: '支持原画质阅读，细节清晰，色彩饱满' },
                { icon: 'ri-community-line', title: '活跃社区', desc: '海量读者评论，一起交流讨论剧情' },
                { icon: 'ri-device-line', title: '多端同步', desc: '手机、平板、电脑无缝切换，阅读进度实时同步' },
                { icon: 'ri-translate-2', title: '精准翻译', desc: '专业翻译团队，忠实还原原作情感与语境' },
              ].map((item) => (
                <div key={item.title} className="rounded-2xl bg-background-50 p-6 border border-background-200/70 hover:border-primary-200 transition-colors">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Business Cooperation */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-start">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  商务合作
                </h2>
                <p className="text-foreground-700 leading-relaxed mb-8">
                  韩漫大全欢迎各类商务合作，包括但不限于版权引进、广告投放、内容授权、品牌联名等。我们拥有庞大的活跃用户群体和精准的内容分发能力，是韩国漫画推广与变现的优质渠道。
                </p>

                <div className="space-y-4">
                  {[
                    { icon: 'ri-service-line', title: '版权合作', desc: '韩国漫画版权引进与正版化合作' },
                    { icon: 'ri-advertisement-line', title: '广告投放', desc: '首页焦点图、分类推荐位、详情页插屏等多形式广告' },
                    { icon: 'ri-file-list-3-line', title: '内容授权', desc: '平台内容对外授权与API接入服务' },
                    { icon: 'ri-global-line', title: '品牌联名', desc: '与韩漫IP进行跨界品牌合作' },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-4 p-4 rounded-xl bg-background-100">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0">
                        <i className={`${item.icon} text-lg`}></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground-950 text-sm mb-1">{item.title}</h4>
                        <p className="text-sm text-foreground-600">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact Form */}
              <div className="rounded-2xl bg-background-100 p-6 md:p-8 border border-background-200/70">
                <h3 className="font-heading text-xl font-bold text-foreground-950 mb-2">联系我们</h3>
                <p className="text-sm text-foreground-600 mb-6">填写下方表单，我们将在3个工作日内回复您</p>

                {formStatus === 'success' ? (
                  <div className="text-center py-8">
                    <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-primary-100">
                      <i className="ri-check-line text-2xl text-primary-600"></i>
                    </div>
                    <h4 className="font-heading text-lg font-bold text-foreground-950 mb-2">提交成功</h4>
                    <p className="text-sm text-foreground-600 mb-4">感谢您的留言，我们会尽快与您联系</p>
                    <button
                      onClick={() => setFormStatus('idle')}
                      className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
                    >
                      再写一条
                    </button>
                  </div>
                ) : (
                  <form
                   
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    <div>
                      <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        id="about-name"
                        name="name"
                        type="text"
                        required
                        autoComplete="name"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        id="about-email"
                        name="email"
                        type="email"
                        required
                        autoComplete="email"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                        placeholder="请输入您的邮箱地址"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">合作类型</label>
                      <select
                        id="about-subject"
                        name="subject"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                      >
                        <option value="">请选择合作类型</option>
                        <option value="copyright">版权合作</option>
                        <option value="advertising">广告投放</option>
                        <option value="content">内容授权</option>
                        <option value="partnership">品牌联名</option>
                        <option value="other">其他</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                      <textarea
                        id="about-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-none"
                        placeholder="请简述您的合作意向或问题，不超过500字"
                      ></textarea>
                      <p className="text-xs text-foreground-500 mt-1">最多500字</p>
                    </div>

                    {/* Honeypot */}
                    <div className="about-form-hp">
                      <input
                        type="text"
                        name="company_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>

                    {formStatus === 'error' && (
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-600 text-sm">
                        <i className="ri-error-warning-line"></i>
                        {formError}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                    >
                      {formStatus === 'submitting' ? '提交中...' : '提交留言'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Contact Info Bar */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="px-4 md:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
              {[
                { icon: 'ri-mail-line', title: '商务邮箱', value: 'hello@knn08.com' },
                { icon: 'ri-customer-service-2-line', title: '客服邮箱', value: 'hello@knn08.com' },
                { icon: 'ri-building-line', title: '公司地址', value: '北京市朝阳区望京SOHO T3' },
              ].map((item) => (
                <div key={item.title} className="rounded-xl bg-background-50 p-6 border border-background-200/70 text-center">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mx-auto mb-3">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-700 mb-1">{item.title}</h4>
                  <p className="text-sm text-foreground-950">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}